export type SidebarLayoutMode =
  | 'classic'
  | 'rail'
  | 'tabs'
  | 'workspaces'
  | 'topmenu'
  | 'sidebar_topmenu';

/** Modes still accepted from stored settings (legacy tabs/workspaces map to classic). */
export const SIDEBAR_LAYOUT_MODES: SidebarLayoutMode[] = [
  'classic',
  'rail',
  'tabs',
  'workspaces',
  'topmenu',
  'sidebar_topmenu',
];

/** Modes offered in Navigation / admin pickers. */
export const SIDEBAR_LAYOUT_SELECTABLE_MODES: Array<
  'classic' | 'rail' | 'topmenu' | 'sidebar_topmenu'
> = ['classic', 'rail', 'topmenu', 'sidebar_topmenu'];

export type SidebarLayoutOption = {
  id: 'classic' | 'rail' | 'topmenu' | 'sidebar_topmenu';
  label: string;
  description: string;
};

export const SIDEBAR_LAYOUT_OPTIONS: SidebarLayoutOption[] = [
  {
    id: 'classic',
    label: 'Classic tree',
    description: 'Full sidebar with expandable groups — best for exploring all modules.',
  },
  {
    id: 'rail',
    label: 'Icon rail',
    description: 'Compact icon strip with flyout panels — more room for content.',
  },
  {
    id: 'topmenu',
    label: 'Top menu',
    description: 'Horizontal navigation in the top bar instead of a left rail.',
  },
  {
    id: 'sidebar_topmenu',
    label: 'Sidebar + top menu',
    description: 'Keep the left sidebar and add the horizontal top menu together.',
  },
];

export function isSidebarLayoutMode(value: unknown): value is SidebarLayoutMode {
  return typeof value === 'string' && SIDEBAR_LAYOUT_MODES.includes(value as SidebarLayoutMode);
}

/** Map legacy / unsupported modes to a selectable layout. */
export function normalizeSidebarLayout(value: unknown): SidebarLayoutMode | null {
  if (!isSidebarLayoutMode(value)) return null;
  if (value === 'tabs' || value === 'workspaces') return 'classic';
  return value;
}

export function resolveSidebarLayout(
  userLayout: SidebarLayoutMode | null | undefined,
  orgLayout: SidebarLayoutMode | undefined,
  isVesselAccount: boolean,
): SidebarLayoutMode {
  const user = normalizeSidebarLayout(userLayout);
  if (user) return user;
  if (isVesselAccount) return 'rail';
  return normalizeSidebarLayout(orgLayout) ?? 'classic';
}

/** True when the horizontal top menu strip should render. */
export function usesTopMenu(layout: SidebarLayoutMode): boolean {
  return layout === 'topmenu' || layout === 'sidebar_topmenu';
}

/** True when the left sidebar should render. */
export function usesSidebar(layout: SidebarLayoutMode): boolean {
  return layout !== 'topmenu';
}
